import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  DataTable,
  PageHeader,
  ErrorState,
  type Column,
  type SortDirection,
} from '../components/common';

type FunctionRun = {
  id: string;
  function_name: string;
  function_version: number;
  success: boolean;
  execution_time_ms?: number;
  model_used?: string;
  created_at?: string;
  token_usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  total_tokens?: number;
  tokens?: number;
  ai_credits?: number;
  multiplier?: number;
  metadata?: any;
};

export default function ApiKeyRuns() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [runs, setRuns] = useState<FunctionRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  
  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const loadRuns = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const res = await fetch(`/api/v2/analytics/api-keys/${id}/runs?${params}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const json = await res.json();
      setRuns(json.results || []);
      setTotalCount(json.pagination?.total_items || 0);
      setTotalTokens(json.data?.total_tokens_all_runs || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadRuns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, page, rowsPerPage, sortBy, sortDir]);

  const handleSearch = () => {
    setPage(0);
    loadRuns();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const columns: Column<FunctionRun>[] = [
    {
      id: 'created_at',
      label: 'Time',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      id: 'function_name',
      label: 'Function',
      sortable: true,
    },
    {
      id: 'function_version',
      label: 'Version',
      sortable: true,
      numeric: true,
    },
    {
      id: 'success',
      label: 'Success',
      sortable: true,
      render: (value) => (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          color={value ? 'success' : 'error'} 
          size="small" 
        />
      ),
    },
    {
      id: 'execution_time_ms',
      label: 'Duration (ms)',
      sortable: true,
      numeric: true,
      render: (value) => value ?? '-',
    },
    {
      id: 'model_used',
      label: 'Model',
      sortable: true,
      render: (value) => value || '-',
    },
    {
      id: 'tokens',
      label: 'Tokens',
      sortable: true,
      numeric: true,
      render: (value, row) => {
        const tokens = row.tokens ?? row.total_tokens ?? row.token_usage?.total_tokens ?? 0;
        return tokens ? tokens.toLocaleString() : '-';
      },
    },
    {
      id: 'ai_credits',
      label: 'AI Credits',
      sortable: true,
      numeric: true,
      render: (value, row) => {
        const credits = row.ai_credits ?? 0;
        return credits ? credits.toFixed(2) : '-';
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Tooltip title="View run details">
          <IconButton 
            size="small"
            onClick={() => navigate(`/dashboard/run/${row.id}`, { state: { run: row } })}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="API Key Runs"
          breadcrumbs={[
            { label: 'API Keys', onClick: () => navigate('/dashboard/api-keys') },
            { label: 'Runs' },
          ]}
        />
        <ErrorState error={error} onRetry={loadRuns} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="API Key Runs"
        subtitle={totalTokens > 0 ? `Total Tokens Used: ${totalTokens.toLocaleString()}` : undefined}
        breadcrumbs={[
          { label: 'API Keys', onClick: () => navigate('/dashboard/api-keys') },
          { label: 'Runs' },
        ]}
        actions={[
          { 
            label: 'Back to API Keys', 
            onClick: () => navigate('/dashboard/api-keys'), 
            icon: <BackIcon />,
            variant: 'outlined',
          },
        ]}
        searchValue={search}
        onSearchChange={setSearch}
        onSearch={handleSearch}
        searchPlaceholder="Search by function name"
        showSearch
      />

      <DataTable
        columns={columns}
        data={runs}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        sortBy={sortBy}
        sortDirection={sortDir}
        onSort={handleSort}
        rowKey="id"
        emptyMessage="No runs found for this API key."
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  );
}